import {
  ORDERED_ARCHES,
  type Arch,
  type Quadrant,
  type TeethArch,
} from "./teeth.type";

export const ORDERED_TEETH_BY_QUADRANT: Record<Quadrant, number[]> = {
  Q1: [18, 17, 16, 15, 14, 13, 12, 11],
  Q2: [21, 22, 23, 24, 25, 26, 27, 28],
  Q3: [38, 37, 36, 35, 34, 33, 32, 31],
  Q4: [41, 42, 43, 44, 45, 46, 47, 48],
};

export const ORDERED_TEETH_BY_ARCH: Record<TeethArch, number[]> = {
  MAXILLARY: [...ORDERED_TEETH_BY_QUADRANT.Q1, ...ORDERED_TEETH_BY_QUADRANT.Q2],
  MANDIBULAR: [
    ...ORDERED_TEETH_BY_QUADRANT.Q3,
    ...ORDERED_TEETH_BY_QUADRANT.Q4,
  ],
};

export const getTeethFromArch = (arch: Arch): number[] => {
  switch (arch) {
    case "MAXILLARY":
      return ORDERED_TEETH_BY_ARCH.MAXILLARY;
    case "MANDIBULAR":
      return ORDERED_TEETH_BY_ARCH.MANDIBULAR;
    case "BIMAXILLARY":
      return [
        ...getTeethFromArch("MAXILLARY"),
        ...getTeethFromArch("MANDIBULAR"),
      ];
    default:
      return [];
  }
};

/**
 * The arches a selection covers whole, and nothing else: empty as soon as one tooth
 * falls outside them or one of their teeth is missing. Naming an arch only holds when
 * the selection is exactly that arch.
 */
export const getArchesCoveredExactly = (
  teeth: ReadonlyArray<number>
): TeethArch[] => {
  const selectedTeeth = new Set(teeth);

  const coveredArches = ORDERED_ARCHES.filter(arch =>
    getTeethFromArch(arch).every(tooth => selectedTeeth.has(tooth))
  );

  const coveredTeethCount = coveredArches.reduce(
    (total, arch) => total + getTeethFromArch(arch).length,
    0
  );

  return coveredTeethCount === selectedTeeth.size ? coveredArches : [];
};

export const getArchFromTooth = (tooth: number): TeethArch | undefined => {
  if (ORDERED_TEETH_BY_ARCH.MAXILLARY.includes(tooth)) {
    return "MAXILLARY";
  } else if (ORDERED_TEETH_BY_ARCH.MANDIBULAR.includes(tooth)) {
    return "MANDIBULAR";
  }
  return undefined;
};

export const haveSameTeeth = (
  firstTeeth: ReadonlyArray<number>,
  secondTeeth: ReadonlyArray<number>
): boolean => {
  const firstSet = new Set(firstTeeth);
  const secondSet = new Set(secondTeeth);

  return (
    firstSet.size === secondSet.size &&
    [...firstSet].every(tooth => secondSet.has(tooth))
  );
};

/**
 * Sort the teeth according to their position in the mouth. The order is from the upper jaw to the lower jaw and from the left to the right.
 * If the teeth are not in the mouth, they will be sorted in ascending order.
 * @param teeth
 * @returns
 */
export const sortTeethByPosition = (teeth: Readonly<number[]>): number[] => {
  return [...teeth].sort((a, b) => {
    const archA = getArchFromTooth(a);
    const archB = getArchFromTooth(b);

    if (archA === undefined && archB === undefined) {
      return a - b;
    }

    if (archA === undefined) {
      return 1;
    }

    if (archB === undefined) {
      return -1;
    }

    if (archA !== archB) {
      return archA === "MAXILLARY" ? -1 : 1;
    }

    const indexA = ORDERED_TEETH_BY_ARCH[archA].indexOf(a);
    const indexB = ORDERED_TEETH_BY_ARCH[archB].indexOf(b);

    return indexA - indexB;
  });
};

export const areTeethAdjacent = (teeth: number[]): boolean => {
  const teethByArch = Object.values(ORDERED_TEETH_BY_ARCH).find(archTeeth =>
    teeth.every(tooth => archTeeth.includes(tooth))
  );
  if (!teethByArch) {
    return false;
  }

  const toothIndexes = teeth.map(tooth =>
    teethByArch.findIndex(archTooth => archTooth === tooth)
  );

  const maxIndex = Math.max(...toothIndexes);
  const minIndex = Math.min(...toothIndexes);

  return maxIndex - minIndex === teeth.length - 1;
};

export const groupAdjacentTeeth = (teeth: Readonly<number[]>): number[][] => {
  const sortedTeeth = sortTeethByPosition(teeth);
  const groups: number[][] = [];

  for (const tooth of sortedTeeth) {
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || !areTeethAdjacent([...lastGroup, tooth])) {
      groups.push([tooth]);
    } else {
      lastGroup.push(tooth);
    }
  }

  return groups;
};

export const groupAndOrderTeethByQuadrants = (
  teeth: Readonly<number[]>
): Record<Quadrant, number[]> => {
  const groupedTeeth: Record<Quadrant, number[]> = {
    Q1: [],
    Q2: [],
    Q3: [],
    Q4: [],
  };

  for (const tooth of teeth) {
    if (ORDERED_TEETH_BY_QUADRANT.Q1.includes(tooth)) {
      groupedTeeth.Q1.push(tooth);
    } else if (ORDERED_TEETH_BY_QUADRANT.Q2.includes(tooth)) {
      groupedTeeth.Q2.push(tooth);
    } else if (ORDERED_TEETH_BY_QUADRANT.Q3.includes(tooth)) {
      groupedTeeth.Q3.push(tooth);
    } else if (ORDERED_TEETH_BY_QUADRANT.Q4.includes(tooth)) {
      groupedTeeth.Q4.push(tooth);
    }
  }

  for (const quadrant in groupedTeeth) {
    groupedTeeth[quadrant as Quadrant].sort((a, b) => a - b);
  }

  return groupedTeeth;
};
